import {
  createEmptyInputState,
  FISHYMAP_CONTRACT_VERSION,
  FISHYMAP_POINT_ICON_SCALE_MAX,
  FISHYMAP_POINT_ICON_SCALE_MIN,
} from "./map-bridge-contract.js";
import { normalizeLayerHighlightMap } from "./map-layer-highlight-style.js";
import { normalizeLootConditionContext } from "./map-loot-condition-context.js";
import { normalizeMemoryBudgetMiB } from "./map-runtime-settings.js";
import { resolveSearchExpression } from "./map-search-contract.js";
import { normalizeThemeColors } from "./map-theme-state.js";
import {
  normalizeCharacterCatalogSnapshot,
  normalizeCharacterIdentityKey,
  normalizeCharacterPresenceSnapshot,
} from "./map-character-presence-contract.js";
import { normalizeVisibleTradeNpcClasses } from "./map-trade-npc-contract.js";

const OPTIONAL_FILTER_RECORD_FIELDS = [
  "layerFilterBindingIdsDisabledByLayer",
  "layerOpacities",
  "layerHighlights",
  "layerConfidenceShowcase",
  "layerClipMasks",
  "layerWaypointConnectionsVisible",
  "layerWaypointLabelsVisible",
  "layerPointIconsVisible",
  "layerPointIconScales",
  "layerPointClusteringEnabled",
];
const FILTER_REPLACEMENT_FIELDS = new Set([
  "searchExpression",
  ...OPTIONAL_FILTER_RECORD_FIELDS,
  "lootConditionSelection",
  "lootConditionContext",
  "tradeNpcClassesVisible",
]);
const UI_REPLACEMENT_FIELDS = new Set([
  "sharedFishState",
  "hoverFactsVisibleByLayer",
]);

function cloneJson(value) {
  if (value == null) {
    return value;
  }
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function normalizeStringList(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const out = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

function normalizeLayerOpacity(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return undefined;
  }
  return Math.min(1, Math.max(0, number));
}

function normalizeLayerOpacityMap(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [key, value] of Object.entries(values)) {
    const layerId = String(key ?? "").trim();
    if (!layerId) {
      continue;
    }
    const opacity = normalizeLayerOpacity(value);
    if (opacity === undefined) {
      continue;
    }
    out[layerId] = opacity;
  }
  return out;
}

function normalizeLayerClipMaskMap(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [key, value] of Object.entries(values)) {
    const layerId = String(key ?? "").trim();
    const maskLayerId = String(value ?? "").trim();
    if (!layerId || !maskLayerId || layerId === maskLayerId) {
      continue;
    }
    out[layerId] = maskLayerId;
  }
  const flattened = {};
  for (const layerId of Object.keys(out)) {
    const seen = new Set([layerId]);
    let cursor = out[layerId];
    let topMaskLayerId = "";
    while (cursor) {
      if (seen.has(cursor)) {
        topMaskLayerId = "";
        break;
      }
      seen.add(cursor);
      const nextMaskLayerId = out[cursor];
      if (!nextMaskLayerId || nextMaskLayerId === cursor) {
        topMaskLayerId = cursor;
        break;
      }
      cursor = nextMaskLayerId;
    }
    if (!topMaskLayerId || topMaskLayerId === layerId) {
      continue;
    }
    flattened[layerId] = topMaskLayerId;
  }
  return flattened;
}

function normalizeLayerBoolMap(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [key, value] of Object.entries(values)) {
    const layerId = String(key ?? "").trim();
    if (!layerId || typeof value !== "boolean") {
      continue;
    }
    out[layerId] = value;
  }
  return out;
}

function normalizeLayerFactBoolMap(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [layerIdInput, factsInput] of Object.entries(values)) {
    const layerId = String(layerIdInput ?? "").trim();
    if (!layerId || !isPlainObject(factsInput)) {
      continue;
    }
    const facts = {};
    for (const [factKeyInput, visible] of Object.entries(factsInput)) {
      const factKey = String(factKeyInput ?? "").trim();
      if (!factKey || typeof visible !== "boolean") {
        continue;
      }
      facts[factKey] = visible;
    }
    if (Object.keys(facts).length) {
      out[layerId] = facts;
    }
  }
  return out;
}

function normalizeLayerStringListMap(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [key, rawValue] of Object.entries(values)) {
    const layerId = String(key ?? "").trim();
    if (!layerId) {
      continue;
    }
    const bindingIds = normalizeStringList(rawValue);
    if (!bindingIds.length) {
      continue;
    }
    out[layerId] = bindingIds;
  }
  return out;
}

function normalizeLayerPointIconScaleMap(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [key, value] of Object.entries(values)) {
    const layerId = String(key ?? "").trim();
    if (!layerId) {
      continue;
    }
    const scale = normalizePointIconScale(value);
    if (scale === undefined) {
      continue;
    }
    out[layerId] = scale;
  }
  return out;
}

function normalizeLootConditionSelection(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [key, value] of Object.entries(values)) {
    const normalizedKey = String(key ?? "").trim();
    const index = Number.parseInt(value, 10);
    if (!normalizedKey || !Number.isInteger(index) || index < 0) {
      continue;
    }
    out[normalizedKey] = index;
  }
  return out;
}

function normalizeFishIds(values) {
  let ids = [];
  if (Array.isArray(values)) {
    ids = values;
  } else if (values && typeof values === "object") {
    ids = Object.entries(values)
      .filter((entry) => entry[1])
      .map((entry) => entry[0]);
  } else {
    return [];
  }
  const out = [];
  const seen = new Set();
  for (const value of ids) {
    const number = Number.parseInt(value, 10);
    if (
      !Number.isFinite(number) ||
      seen.has(number) ||
      number <= 0
    ) {
      continue;
    }
    seen.add(number);
    out.push(number);
  }
  return out;
}

function normalizeSharedFishFilterStateValue(value) {
  const caughtIds = normalizeFishIds(value?.caughtIds);
  const favouriteIds = normalizeFishIds(value?.favouriteIds);
  return {
    caughtIds,
    favouriteIds,
    showDried: value?.showDried === true || value?.show_dried === true,
  };
}

export function normalizeNullableString(value) {
  if (value == null) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized || null;
}

export function normalizeSelectionPointKind(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (
    normalized === "clicked" ||
    normalized === "waypoint" ||
    normalized === "bookmark" ||
    normalized === "character"
  ) {
    return normalized;
  }
  return null;
}

export function normalizeSelectionHistoryBehavior(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "append" || normalized === "navigate") {
    return normalized;
  }
  return null;
}

export function normalizeRestoreView(value) {
  if (!isPlainObject(value)) {
    return undefined;
  }
  const viewMode = value.viewMode === "3d" ? "3d" : "2d";
  const sourceCamera = isPlainObject(value.camera) ? value.camera : {};
  const camera = {};
  const numericKeys = [
    "centerWorldX",
    "centerWorldZ",
    "zoom",
    "pivotWorldX",
    "pivotWorldY",
    "pivotWorldZ",
    "yaw",
    "pitch",
    "distance",
  ];
  for (const key of numericKeys) {
    if (!hasOwn(sourceCamera, key)) {
      continue;
    }
    const value = sourceCamera[key];
    if (value == null || value === "") {
      continue;
    }
    const number = Number(value);
    if (Number.isFinite(number)) {
      camera[key] = number;
    }
  }
  return {
    viewMode,
    camera,
  };
}

function normalizeNullableViewMode(value) {
  if (value == null || value === "") {
    return null;
  }
  if (value === "2d" || value === "3d") {
    return value;
  }
  return undefined;
}

function normalizePointIconScale(value) {
  if (value == null || value === "") {
    return undefined;
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return undefined;
  }
  return Math.min(FISHYMAP_POINT_ICON_SCALE_MAX, Math.max(FISHYMAP_POINT_ICON_SCALE_MIN, number));
}

function normalizeBookmarkCoordinate(value) {
  if (value == null || value === "") {
    return undefined;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function normalizeBookmarkLayerSamplesState(values) {
  return Array.isArray(values)
    ? values.filter((sample) => isPlainObject(sample)).map((sample) => cloneJson(sample))
    : [];
}

function normalizeWorldPointCommand(value) {
  if (!isPlainObject(value)) {
    return undefined;
  }
  const worldX = normalizeBookmarkCoordinate(value.worldX);
  const worldZ = normalizeBookmarkCoordinate(value.worldZ);
  if (worldX === undefined || worldZ === undefined) {
    return undefined;
  }
  const pointKind = normalizeSelectionPointKind(value.pointKind);
  const pointLabel = normalizeNullableString(value.pointLabel);
  const elementKind = normalizeNullableString(value.elementKind);
  const historyBehavior = normalizeSelectionHistoryBehavior(value.historyBehavior);
  const characterIdentityKey =
    value.characterIdentityKey == null || value.characterIdentityKey === ""
      ? null
      : normalizeCharacterIdentityKey(
          value.characterIdentityKey,
          "commands.selectWorldPoint.characterIdentityKey",
        );
  if (
    characterIdentityKey != null &&
    (elementKind !== "character" || pointKind !== "character")
  ) {
    throw new TypeError(
      "commands.selectWorldPoint.characterIdentityKey requires a Character target",
    );
  }
  return {
    ...(elementKind != null ? { elementKind } : {}),
    ...(characterIdentityKey != null ? { characterIdentityKey } : {}),
    worldX,
    worldZ,
    ...(pointKind != null ? { pointKind } : {}),
    ...(pointLabel != null ? { pointLabel } : {}),
    ...(historyBehavior != null ? { historyBehavior } : {}),
  };
}

function normalizeSemanticFieldCommand(value) {
  if (!isPlainObject(value)) {
    return undefined;
  }
  const layerId = String(value.layerId || "").trim();
  const fieldId = Number.parseInt(value.fieldId, 10);
  const targetKey = normalizeNullableString(value.targetKey);
  if (!layerId || !Number.isFinite(fieldId)) {
    return undefined;
  }
  return {
    layerId,
    fieldId,
    ...(targetKey != null ? { targetKey } : {}),
  };
}

function normalizeBookmarksState(values) {
  const entries = Array.isArray(values) ? values : [];
  const normalized = [];
  const seen = new Set();
  for (const entry of entries) {
    const id = String(entry?.id || "").trim();
    const worldX = normalizeBookmarkCoordinate(entry?.worldX);
    const worldY = normalizeBookmarkCoordinate(entry?.worldY);
    const worldZ = normalizeBookmarkCoordinate(entry?.worldZ);
    if (
      !id ||
      worldX === undefined ||
      worldY === undefined ||
      worldZ === undefined ||
      seen.has(id)
    ) {
      continue;
    }
    seen.add(id);
    const label = normalizeNullableString(entry?.label);
    const pointLabel = normalizeNullableString(entry?.pointLabel);
    const layerSamples = normalizeBookmarkLayerSamplesState(entry?.layerSamples);
    const zoneRgb = Number.parseInt(entry?.zoneRgb, 10);
    const createdAt = normalizeNullableString(entry?.createdAt);
    normalized.push({
      id,
      ...(label != null ? { label } : {}),
      ...(pointLabel != null ? { pointLabel } : {}),
      worldX,
      worldY,
      worldZ,
      ...(layerSamples.length ? { layerSamples } : {}),
      ...(Number.isFinite(zoneRgb) ? { zoneRgb } : {}),
      ...(createdAt != null ? { createdAt } : {}),
    });
  }
  return normalized;
}

export function normalizeStatePatch(patch = {}) {
  const normalized = {
    version: FISHYMAP_CONTRACT_VERSION,
  };

  if (isPlainObject(patch.theme)) {
    normalized.theme = {};
    if (typeof patch.theme.name === "string" && patch.theme.name.trim()) {
      normalized.theme.name = patch.theme.name.trim();
    }
    const colors = normalizeThemeColors(patch.theme.colors);
    if (colors) {
      normalized.theme.colors = colors;
    }
    if (!normalized.theme.name && !normalized.theme.colors) {
      delete normalized.theme;
    }
  }

  if (isPlainObject(patch.filters)) {
    normalized.filters = {};
    if (hasOwn(patch.filters, "searchExpression")) {
      normalized.filters.searchExpression = resolveSearchExpression(patch.filters.searchExpression);
    }
    if (hasOwn(patch.filters, "layerIdsVisible")) {
      normalized.filters.layerIdsVisible = normalizeStringList(patch.filters.layerIdsVisible);
    }
    if (hasOwn(patch.filters, "layerIdsOrdered")) {
      normalized.filters.layerIdsOrdered = normalizeStringList(patch.filters.layerIdsOrdered);
    }
    if (hasOwn(patch.filters, "layerFilterBindingIdsDisabledByLayer")) {
      normalized.filters.layerFilterBindingIdsDisabledByLayer = normalizeLayerStringListMap(
        patch.filters.layerFilterBindingIdsDisabledByLayer,
      );
    }
    if (hasOwn(patch.filters, "layerOpacities")) {
      normalized.filters.layerOpacities = normalizeLayerOpacityMap(patch.filters.layerOpacities);
    }
    if (hasOwn(patch.filters, "layerHighlights")) {
      normalized.filters.layerHighlights = normalizeLayerHighlightMap(
        patch.filters.layerHighlights,
      );
    }
    if (hasOwn(patch.filters, "layerConfidenceShowcase")) {
      normalized.filters.layerConfidenceShowcase = normalizeLayerBoolMap(
        patch.filters.layerConfidenceShowcase,
      );
    }
    if (hasOwn(patch.filters, "layerClipMasks")) {
      normalized.filters.layerClipMasks = normalizeLayerClipMaskMap(patch.filters.layerClipMasks);
    }
    if (hasOwn(patch.filters, "layerWaypointConnectionsVisible")) {
      normalized.filters.layerWaypointConnectionsVisible = normalizeLayerBoolMap(
        patch.filters.layerWaypointConnectionsVisible,
      );
    }
    if (hasOwn(patch.filters, "layerWaypointLabelsVisible")) {
      normalized.filters.layerWaypointLabelsVisible = normalizeLayerBoolMap(
        patch.filters.layerWaypointLabelsVisible,
      );
    }
    if (hasOwn(patch.filters, "layerPointIconsVisible")) {
      normalized.filters.layerPointIconsVisible = normalizeLayerBoolMap(
        patch.filters.layerPointIconsVisible,
      );
    }
    if (hasOwn(patch.filters, "layerPointIconScales")) {
      normalized.filters.layerPointIconScales = normalizeLayerPointIconScaleMap(
        patch.filters.layerPointIconScales,
      );
    }
    if (hasOwn(patch.filters, "layerPointClusteringEnabled")) {
      normalized.filters.layerPointClusteringEnabled = normalizeLayerBoolMap(
        patch.filters.layerPointClusteringEnabled,
      );
    }
    if (hasOwn(patch.filters, "tradeNpcClassesVisible")) {
      normalized.filters.tradeNpcClassesVisible = normalizeVisibleTradeNpcClasses(
        patch.filters.tradeNpcClassesVisible,
      );
    }
    if (hasOwn(patch.filters, "filterOnlyActiveConditionGroups")) {
      normalized.filters.filterOnlyActiveConditionGroups =
        patch.filters.filterOnlyActiveConditionGroups !== false;
    }
    if (hasOwn(patch.filters, "lootConditionSelection")) {
      normalized.filters.lootConditionSelection = normalizeLootConditionSelection(
        patch.filters.lootConditionSelection,
      );
    }
    if (hasOwn(patch.filters, "lootConditionContext")) {
      normalized.filters.lootConditionContext = normalizeLootConditionContext(
        patch.filters.lootConditionContext,
      );
    }
    if (!Object.keys(normalized.filters).length) {
      delete normalized.filters;
    }
  }

  if (isPlainObject(patch.ui)) {
    normalized.ui = {};
    for (const key of [
      "showPoints",
      "showPointIcons",
      "threeDEnabled",
      "autoAdjustView",
      "sampleFromFilteredElements",
      "effectsEnabled",
      "focusWavesEnabled",
    ]) {
      if (typeof patch.ui[key] === "boolean") {
        normalized.ui[key] = patch.ui[key];
      }
    }
    if (hasOwn(patch.ui, "pointIconScale")) {
      const pointIconScale = normalizePointIconScale(patch.ui.pointIconScale);
      if (pointIconScale !== undefined) {
        normalized.ui.pointIconScale = pointIconScale;
      }
    }
    if (hasOwn(patch.ui, "sharedFishState")) {
      const sharedFishState = normalizeSharedFishFilterStateValue(patch.ui.sharedFishState);
      normalized.ui.sharedFishState = {
        caughtIds: sharedFishState.caughtIds,
        favouriteIds: sharedFishState.favouriteIds,
        showDried: sharedFishState.showDried,
      };
    }
    if (hasOwn(patch.ui, "viewMode")) {
      const viewMode = normalizeNullableViewMode(patch.ui.viewMode);
      if (viewMode !== undefined) {
        normalized.ui.viewMode = viewMode;
      }
    }
    if (hasOwn(patch.ui, "hoverFactsVisibleByLayer")) {
      normalized.ui.hoverFactsVisibleByLayer = normalizeLayerFactBoolMap(
        patch.ui.hoverFactsVisibleByLayer,
      );
    }
    if (hasOwn(patch.ui, "bookmarkSelectedIds")) {
      normalized.ui.bookmarkSelectedIds = normalizeStringList(patch.ui.bookmarkSelectedIds);
    }
    if (hasOwn(patch.ui, "bookmarks")) {
      normalized.ui.bookmarks = normalizeBookmarksState(patch.ui.bookmarks);
    }
    if (!Object.keys(normalized.ui).length) {
      delete normalized.ui;
    }
  }

  if (isPlainObject(patch.settings)) {
    normalized.settings = {};
    if (hasOwn(patch.settings, "memoryBudgetMiB")) {
      const memoryBudgetMiB = normalizeMemoryBudgetMiB(patch.settings.memoryBudgetMiB);
      if (memoryBudgetMiB !== undefined) {
        normalized.settings.memoryBudgetMiB = memoryBudgetMiB;
      }
    }
    if (!Object.keys(normalized.settings).length) {
      delete normalized.settings;
    }
  }

  if (hasOwn(patch, "characterCatalog")) {
    normalized.characterCatalog = normalizeCharacterCatalogSnapshot(patch.characterCatalog);
  }

  if (hasOwn(patch, "characterPresence")) {
    normalized.characterPresence = normalizeCharacterPresenceSnapshot(patch.characterPresence);
  }

  if (isPlainObject(patch.commands)) {
    normalized.commands = {};
    if (typeof patch.commands.resetView === "boolean") {
      normalized.commands.resetView = patch.commands.resetView;
    }
    if (typeof patch.commands.focusSearch === "boolean") {
      normalized.commands.focusSearch = patch.commands.focusSearch;
    }
    if (patch.commands.setViewMode === "2d" || patch.commands.setViewMode === "3d") {
      normalized.commands.setViewMode = patch.commands.setViewMode;
    }
    if (hasOwn(patch.commands, "selectZoneRgb")) {
      const selectZoneRgb = Number.parseInt(patch.commands.selectZoneRgb, 10);
      if (Number.isFinite(selectZoneRgb)) {
        normalized.commands.selectZoneRgb = selectZoneRgb;
      }
    }
    if (hasOwn(patch.commands, "selectSemanticField")) {
      const selectSemanticField = normalizeSemanticFieldCommand(patch.commands.selectSemanticField);
      if (selectSemanticField) {
        normalized.commands.selectSemanticField = selectSemanticField;
      }
    }
    if (hasOwn(patch.commands, "selectWorldPoint")) {
      const selectWorldPoint = normalizeWorldPointCommand(patch.commands.selectWorldPoint);
      if (selectWorldPoint) {
        normalized.commands.selectWorldPoint = selectWorldPoint;
      }
    }
    if (hasOwn(patch.commands, "restoreView")) {
      const restoreView = normalizeRestoreView(patch.commands.restoreView);
      if (restoreView) {
        normalized.commands.restoreView = restoreView;
      }
    }
    if (normalized.commands.restoreView && normalized.commands.setViewMode) {
      normalized.commands.restoreView.viewMode = normalized.commands.setViewMode;
    }
    if (!Object.keys(normalized.commands).length) {
      delete normalized.commands;
    }
  }

  return normalized;
}

export function patchHasStateFields(patch) {
  return !!(
    patch.theme ||
    patch.filters ||
    patch.ui ||
    patch.settings ||
    patch.characterCatalog ||
    patch.characterPresence
  );
}

export function patchHasCommands(patch) {
  return !!(patch.commands && Object.keys(patch.commands).length);
}

function mergePatchBranch(target, source, replacementFields = null) {
  const out = { ...(target || {}) };
  for (const [key, value] of Object.entries(source || {})) {
    if (replacementFields?.has(key)) {
      out[key] = value;
      continue;
    }
    if (Array.isArray(value)) {
      out[key] = value.slice();
      continue;
    }
    if (isPlainObject(value)) {
      out[key] = mergePatchBranch(out[key], value);
      continue;
    }
    out[key] = value;
  }
  return out;
}

export function mergeStatePatch(left, right) {
  const base = normalizeStatePatch(left);
  const patch = normalizeStatePatch(right);
  const out = {
    version: FISHYMAP_CONTRACT_VERSION,
  };
  if (base.theme || patch.theme) {
    out.theme = mergePatchBranch(base.theme, patch.theme);
  }
  if (base.filters || patch.filters) {
    out.filters = mergePatchBranch(base.filters, patch.filters, FILTER_REPLACEMENT_FIELDS);
  }
  if (base.ui || patch.ui) {
    out.ui = mergePatchBranch(base.ui, patch.ui, UI_REPLACEMENT_FIELDS);
  }
  if (base.settings || patch.settings) {
    out.settings = mergePatchBranch(base.settings, patch.settings);
  }
  if (base.characterCatalog || patch.characterCatalog) {
    out.characterCatalog = cloneJson(patch.characterCatalog || base.characterCatalog);
  }
  if (base.characterPresence || patch.characterPresence) {
    out.characterPresence = cloneJson(patch.characterPresence || base.characterPresence);
  }
  if (base.commands || patch.commands) {
    out.commands = mergePatchBranch(base.commands, patch.commands);
  }
  return normalizeStatePatch(out);
}

export function applyStatePatch(inputState, patch) {
  const merged = mergeStatePatch(
    cloneJson(inputState || createEmptyInputState()),
    patch,
  );
  const next = createEmptyInputState();
  for (const branch of [
    "theme",
    "filters",
    "ui",
    "settings",
    "characterCatalog",
    "characterPresence",
  ]) {
    if (merged[branch]) {
      next[branch] = { ...next[branch], ...merged[branch] };
    }
  }
  for (const field of OPTIONAL_FILTER_RECORD_FIELDS) {
    if (!Object.keys(next.filters[field] || {}).length) {
      next.filters[field] = undefined;
    }
  }
  if (next.ui.threeDEnabled === false) {
    next.ui.viewMode = "2d";
  }

  return inputState && JSON.stringify(next) === JSON.stringify(inputState)
    ? inputState
    : next;
}
